import { useQuery } from '@tanstack/react-query'
import { Users, FileText, ClipboardList, Smartphone, Clock, AlertTriangle } from 'lucide-react'
import { reportsApi, loginHistoryApi, submissionsApi, formsApi } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'

export default function DashboardPage() {
  const { data: ovRes } = useQuery({
    queryKey: ['reports-overview-dash'],
    queryFn: () => reportsApi.overview(),
    staleTime: 2 * 60 * 1000,
  })
  const { data: loginsRes } = useQuery({
    queryKey: ['login-history-dash'],
    queryFn: () => loginHistoryApi.list({ page: 1, page_size: 7 }),
    staleTime: 60 * 1000,
  })
  const { data: subsRes } = useQuery({
    queryKey: ['submissions-dash'],
    queryFn: () => submissionsApi.listAll({ page: 1, page_size: 6 }),
    staleTime: 60 * 1000,
  })
  const { data: formsRes } = useQuery({
    queryKey: ['forms-dash'],
    queryFn: () => formsApi.list({ page: 1, page_size: 1 }),
    staleTime: 5 * 60 * 1000,
  })

  const ov = ovRes?.data as any
  const logins: any[] = loginsRes?.data ?? []
  const subs: any[] = (subsRes?.data as any)?.items ?? []
  const formsTotal = (formsRes?.data as any)?.total ?? '—'

  const thisMonth = ov?.submissions?.this_month ?? 0
  const lastMonth = ov?.submissions?.last_month ?? 0
  const monthPct = lastMonth > 0
    ? Math.round(((thisMonth - lastMonth) / lastMonth) * 100)
    : null
  const pendingTasks = ov?.workflows?.tasks_pending ?? 0

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">Resumo operacional do tenant</p>
        </div>
      </div>

      {/* Alerta tarefas pendentes */}
      {pendingTasks > 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span><strong>{pendingTasks}</strong> tarefa{pendingTasks !== 1 ? 's' : ''} de workflow pendente{pendingTasks !== 1 ? 's' : ''} de aprovação.</span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Utilizadores Ativos"
          value={ov?.users?.active ?? '—'}
          sub={`${ov?.users?.total ?? '—'} total`}
          icon={<Users className="h-4 w-4" />}
          color="blue"
        />
        <KpiCard
          label="Formulários"
          value={formsTotal}
          sub="configurados"
          icon={<FileText className="h-4 w-4" />}
          color="purple"
        />
        <KpiCard
          label="Submissões este mês"
          value={thisMonth}
          sub={monthPct !== null
            ? `${monthPct >= 0 ? '+' : ''}${monthPct}% vs mês anterior`
            : `${ov?.submissions?.total ?? '—'} no total`}
          trend={monthPct}
          icon={<ClipboardList className="h-4 w-4" />}
          color="green"
        />
        <KpiCard
          label="Dispositivos Ativos"
          value={ov?.devices?.active ?? '—'}
          sub={`${ov?.devices?.total ?? '—'} registados`}
          icon={<Smartphone className="h-4 w-4" />}
          color="orange"
        />
      </div>

      {/* Activity + Submissions */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Recent logins */}
        <div className="card">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
            <Clock className="h-4 w-4 text-gray-400" /> Acessos Recentes
          </h3>
          <div className="mt-3">
            {logins.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">Sem registos</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {logins.map((l: any) => (
                    <tr key={l.id} className="border-b border-gray-50 last:border-0">
                      <td className="py-2 pr-3">
                        <p className="font-medium text-gray-800 leading-tight">{l.user_name || l.user_email || '—'}</p>
                        <p className="text-xs text-gray-400">{l.device_name || 'Web'}</p>
                      </td>
                      <td className="py-2 text-right">
                        <p className="text-xs text-gray-500">{formatDateTime(l.login_at)}</p>
                        <span className={`text-xs font-medium ${l.success ? 'text-green-600' : 'text-red-500'}`}>
                          {l.success ? '✓ OK' : '✗ Falhou'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Recent submissions */}
        <div className="card">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
            <ClipboardList className="h-4 w-4 text-gray-400" /> Submissões Recentes
          </h3>
          <div className="mt-3">
            {subs.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">Sem submissões</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {subs.map((s: any) => (
                    <tr key={s.id} className="border-b border-gray-50 last:border-0">
                      <td className="py-2 pr-3">
                        <p className="font-medium text-gray-800 leading-tight">{s.form_name || '—'}</p>
                        <p className="text-xs text-gray-400">{s.submitted_by_name || '—'}</p>
                      </td>
                      <td className="py-2 text-right">
                        <p className="text-xs text-gray-500 whitespace-nowrap">{formatDateTime(s.submitted_at)}</p>
                        <StatusBadge status={s.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const COLOR_MAP: Record<string, { bg: string; icon: string; text: string }> = {
  blue:   { bg: 'bg-blue-50',   icon: 'text-blue-500',   text: '' },
  purple: { bg: 'bg-purple-50', icon: 'text-purple-500', text: '' },
  green:  { bg: 'bg-green-50',  icon: 'text-green-500',  text: '' },
  orange: { bg: 'bg-orange-50', icon: 'text-orange-500', text: '' },
}

function KpiCard({ label, value, sub, icon, color, trend }: {
  label: string
  value: number | string
  sub?: string
  icon: React.ReactNode
  color: keyof typeof COLOR_MAP
  trend?: number | null
}) {
  const c = COLOR_MAP[color]
  return (
    <div className="card flex items-start justify-between py-4">
      <div>
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className="mt-1 text-3xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString('pt-PT') : value}</p>
        {sub && (
          <p className={`mt-1 text-xs ${
            trend != null
              ? trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-500' : 'text-gray-500'
              : 'text-gray-500'
          }`}>
            {trend != null && (trend > 0 ? '↑ ' : trend < 0 ? '↓ ' : '')}{sub}
          </p>
        )}
      </div>
      <div className={`rounded-lg p-2 ${c.bg} ${c.icon}`}>{icon}</div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    approved:    'bg-green-100 text-green-700',
    rejected:    'bg-red-100 text-red-600',
    submitted:   'bg-blue-100 text-blue-700',
    pending:     'bg-amber-100 text-amber-700',
    in_progress: 'bg-indigo-100 text-indigo-700',
    draft:       'bg-gray-100 text-gray-600',
  }
  const labels: Record<string, string> = {
    approved: 'Aprovado', rejected: 'Rejeitado', submitted: 'Submetido',
    pending: 'Pendente', in_progress: 'Em curso', draft: 'Rascunho',
  }
  return (
    <span className={`inline-block rounded-full px-1.5 py-0.5 text-xs font-medium ${map[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {labels[status] ?? status}
    </span>
  )
}

